import * as Dialog from '@radix-ui/react-dialog';
import * as Tabs from '@radix-ui/react-tabs';
import { useCallback, useEffect, useState } from 'react';
import {
  Controller,
  FieldErrors,
  FieldPath,
  FormProvider,
  UseFormReturn,
} from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import {
  classNames,
  formatCurrency,
  usePhoneXlWidth,
  useTabletWidth,
  useZodForm,
} from '../../utils';
import { Button } from '../Button';
import { DownPaymentInput } from '../forms/down-payment-input';
import {
  DropdownSelectInput,
  SelectOption,
} from '../forms/dropdown-select-input';
import { FormattedInput } from '../forms/formatted-input';
import { InputRow } from '../forms/input-row';
import { MoneyInput } from '../forms/money-input';

import { omit } from 'lodash';
import { ReactComponent as CloseIcon } from '../icons/close.svg';
import { ReactComponent as DetailsIcon } from '../icons/details.svg';
import EquityFinancingHome from '../../images/EquityFinancingHome.svg';
import OtherRefinancingHome from '../../images/OtherRefinancingHome.svg';
import LoadingSpinner from '../../images/LoadingSpinner.svg';
import ResetIcon from '../../images/ResetIcon.svg';
import {
  InteractiveQuoteLoanCategory,
  LoanTerm,
  OccupancyType,
  ResidenceType,
  SortDirection,
  SortField,
  TransactionPurpose,
  TransactionPurposeTab,
} from '../RatesTool/types';
import { RatesToolDisplayContainer } from '../RatesTool/rates-tool-rate-display';
import { DropdownBooleanSelectInput, DropdownRangeSliderInput } from '../forms';
import { LabelTooltip } from '../LabelTooltip';
import SelectArrow from '../../images/select-arrow.svg';
import {
  InteractiveRatesFormProps,
  InteractiveRatesFormShape,
  cashOutRefinanceFormConfig,
  dropdownSelectLabelClassName,
  getTransactionPurpose,
  interactiveRatesFormValidationSchema,
  purchaseFormConfig,
  rateTermRefinanceFormConfig,
  refiOptions,
  tabOptions,
  useSliderRanges,
  type FieldConfig,
  type FormConfig,
} from './utils';

function RenderFormField({
  field,
  submitIfChanged,
  loading,
  methods,
  occupancyTypeOptions,
  residenceTypeOptions,
  numUnitsOptions,
  ficoOptions,
  propertyType,
  setPropertyType,
}: {
  field:
    | FieldPath<InteractiveRatesFormShape>
    | FieldConfig<InteractiveRatesFormShape>;
  occupancyTypeOptions: SelectOption[];
  residenceTypeOptions: SelectOption[];
  numUnitsOptions: SelectOption[];
  ficoOptions: SelectOption[];
  loading: boolean;
  methods: UseFormReturn<InteractiveRatesFormShape>;
  submitIfChanged: (
    name: FieldPath<InteractiveRatesFormShape>
  ) => (e?: React.FocusEvent<HTMLInputElement, Element>) => void;
  propertyType: ResidenceType | undefined;
  setPropertyType: React.Dispatch<
    React.SetStateAction<ResidenceType | undefined>
  >;
}) {
  const fieldName = typeof field === 'string' ? field : field.field;

  switch (fieldName) {
    case 'purchasePrice':
      return (
        <Controller
          name="purchasePrice"
          control={methods.control}
          render={({ field }) => (
            <InputRow label="Purchase price">
              <MoneyInput
                {...omit(field, 'ref')}
                disabled={loading}
                onBlur={submitIfChanged('purchasePrice')}
              />
            </InputRow>
          )}
        />
      );
    case 'downPayment':
      return (
        <Controller
          name="downPayment"
          control={methods.control}
          render={({ field }) => (
            <InputRow label="Down payment">
              <DownPaymentInput
                {...omit(field, 'ref')}
                disabled={loading}
                loading={loading}
                total={methods.getValues('purchasePrice')}
                onChange={(val, e) => {
                  methods.setValue(
                    'downPayment',
                    val === 0 ? 0 : val != null ? val : null
                  );
                  submitIfChanged('downPayment')(e);
                }}
              />
            </InputRow>
          )}
        />
      );
    case 'propertyZip':
      return (
        <Controller
          name="propertyZip"
          control={methods.control}
          render={({ field }) => (
            <InputRow label="Zip code">
              <FormattedInput
                {...omit(field, 'ref')}
                disabled={loading}
                format="#####"
                onBlur={submitIfChanged('propertyZip')}
              />
            </InputRow>
          )}
        />
      );

    case 'ficoScore':
      return (
        <Controller
          control={methods.control}
          name="ficoScore"
          render={({ field }) => {
            return (
              <InputRow label="Credit score">
                <DropdownSelectInput
                  {...omit(field, 'ref')}
                  options={ficoOptions}
                  label="Credit score"
                  includeCheckboxes={false}
                  labelVariant="plain"
                  disabled={loading}
                  onChange={(val) => {
                    methods.setValue('ficoScore', val as number);
                    submitIfChanged('ficoScore')(undefined);
                  }}
                  dropdownClassName={'w-full'}
                />
              </InputRow>
            );
          }}
        />
      );
    case 'annualIncome':
      return (
        <Controller
          name="annualIncome"
          control={methods.control}
          render={({ field }) => {
            return (
              <InputRow label="Annual income">
                <MoneyInput
                  {...omit(field, 'ref')}
                  disabled={loading}
                  onBlur={submitIfChanged('annualIncome')}
                />
              </InputRow>
            );
          }}
        />
      );

    case 'monthlyDebt':
      return (
        <Controller
          name="monthlyDebt"
          control={methods.control}
          render={({ field }) => (
            <InputRow label="Monthly debt">
              <MoneyInput
                {...omit(field, 'ref')}
                disabled={loading}
                onBlur={submitIfChanged('monthlyDebt')}
              />
            </InputRow>
          )}
        />
      );

    case 'totalAssets':
      return (
        <Controller
          name="totalAssets"
          control={methods.control}
          render={({ field }) => (
            <InputRow label="Assets">
              <MoneyInput
                {...omit(field, 'ref')}
                disabled={loading}
                onBlur={submitIfChanged('totalAssets')}
              />
            </InputRow>
          )}
        />
      );

    case 'occupancyType':
      return (
        <Controller
          control={methods.control}
          name="occupancyType"
          render={({ field }) => {
            return (
              <InputRow label="Occupancy">
                <DropdownSelectInput
                  {...omit(field, 'ref')}
                  options={occupancyTypeOptions}
                  label="Occupancy"
                  includeCheckboxes={false}
                  labelVariant="plain"
                  disabled={loading}
                  onChange={(val) => {
                    methods.setValue('occupancyType', val as OccupancyType);
                    submitIfChanged('occupancyType')(undefined);
                  }}
                  dropdownClassName={'w-full'}
                />
              </InputRow>
            );
          }}
        />
      );

    case 'residenceType':
      return (
        <Controller
          control={methods.control}
          name="residenceType"
          render={({ field }) => {
            return (
              <InputRow label="Property type">
                <DropdownSelectInput
                  {...omit(field, 'ref')}
                  options={residenceTypeOptions}
                  label="Property type"
                  includeCheckboxes={false}
                  labelVariant="plain"
                  disabled={loading}
                  onChange={(val) => {
                    if (val !== ResidenceType.MultiFamily) {
                      methods.setValue('numUnits', 1);
                    }
                    methods.setValue('residenceType', val as ResidenceType);
                    submitIfChanged('residenceType')(undefined);
                    submitIfChanged('numUnits')(undefined);
                    setPropertyType(val as ResidenceType);
                  }}
                  dropdownClassName={'w-full'}
                />
              </InputRow>
            );
          }}
        />
      );

    case 'numUnits':
      return (
        <Controller
          control={methods.control}
          name="numUnits"
          render={({ field }) => {
            return (
              <InputRow label="Units">
                <DropdownSelectInput
                  {...omit(field, 'ref')}
                  options={numUnitsOptions}
                  label="Units"
                  includeCheckboxes={false}
                  labelVariant="plain"
                  disabled={
                    loading || propertyType === ResidenceType.MultiFamily
                      ? false
                      : true
                  }
                  onChange={(val) => {
                    methods.setValue('numUnits', val as number);
                    submitIfChanged('numUnits')(undefined);
                  }}
                  dropdownClassName={'w-full'}
                />
              </InputRow>
            );
          }}
        />
      );
    case 'isTargetCashToClose':
      return (
        <Controller
          control={methods.control}
          name="isTargetCashToClose"
          render={({ field }) => {
            return (
              <InputRow label="Closing costs" className="flex-[2]">
                <DropdownBooleanSelectInput
                  {...omit(field, 'ref')}
                  options={[
                    {
                      value: true,
                      label: 'Pay no cash upfront by financing closing costs',
                    },
                    {
                      value: false,
                      label:
                        'Pay closing costs upfront to reduce monthly payments',
                    },
                  ]}
                  label="Closing costs"
                  useCustomLabels={true}
                  includeCheckboxes={false}
                  disabled={loading}
                  onChange={(val) => {
                    methods.setValue('isTargetCashToClose', !!val);
                    submitIfChanged('isTargetCashToClose')(undefined);
                  }}
                  inputClassName="max-w-[172px]"
                />
              </InputRow>
            );
          }}
        />
      );

    case 'monthlyPropertyTaxes':
      return (
        <Controller
          name="monthlyPropertyTaxes"
          control={methods.control}
          render={({ field }) => (
            <InputRow label="Property taxes" className="flex-[2]">
              <MoneyInput
                {...omit(field, 'ref')}
                disabled={loading}
                onBlur={submitIfChanged('monthlyPropertyTaxes')}
                placeholder="Optional"
                //mounting issues when submitting so force into string
                value={String(field.value ?? '')}
                rightIcon={<div className="text-sidewalk-300">/mo</div>}
              />
            </InputRow>
          )}
        />
      );

    case 'monthlyHoi':
      return (
        <Controller
          name="monthlyHoi"
          control={methods.control}
          render={({ field }) => (
            <InputRow label="Homeowner's insurance" className={'flex-[2]'}>
              <MoneyInput
                {...omit(field, 'ref')}
                disabled={loading}
                onBlur={submitIfChanged('monthlyHoi')}
                placeholder="Optional"
                rightIcon={<div className="text-sidewalk-300">/mo</div>}
                value={String(field.value ?? '')}
              />
            </InputRow>
          )}
        />
      );

    case 'monthlyHoa':
      return (
        <Controller
          name="monthlyHoa"
          control={methods.control}
          render={({ field }) => (
            <InputRow label="Property fees" className="flex-[2]">
              <MoneyInput
                {...omit(field, 'ref')}
                disabled={loading}
                onBlur={submitIfChanged('monthlyHoa')}
                rightIcon={<div className="text-sidewalk-300">/mo</div>}
                value={String(field.value ?? '')}
              />
            </InputRow>
          )}
        />
      );
    case 'sellerCredits':
      return (
        <Controller
          name="sellerCredits"
          control={methods.control}
          render={({ field }) => (
            <InputRow
              label="Seller credits"
              className="flex-[1] w-full sm:w-[100px]"
              labelClassName="whitespace-nowrap"
            >
              <MoneyInput
                {...omit(field, 'ref')}
                disabled={loading}
                onBlur={submitIfChanged('sellerCredits')}
                value={field.value ?? undefined}
              />
            </InputRow>
          )}
        />
      );
    case 'earnestMoneyDeposit':
      return (
        <Controller
          name="earnestMoneyDeposit"
          control={methods.control}
          render={({ field }) => (
            <InputRow
              label="EMD"
              className="flex-[1] w-full sm:w-[100px]"
              labelClassName="whitespace-nowrap"
            >
              <MoneyInput
                {...omit(field, 'ref')}
                disabled={loading}
                onBlur={submitIfChanged('earnestMoneyDeposit')}
                value={field.value ?? undefined}
              />
            </InputRow>
          )}
        />
      );
    case 'isEscrowWaived':
      return (
        <Controller
          name="isEscrowWaived"
          control={methods.control}
          render={({ field }) => (
            <InputRow
              label="Escrow waiver"
              className="flex-[1] w-full sm:w-[100px]"
              labelClassName="whitespace-nowrap"
            >
              <DropdownBooleanSelectInput
                {...omit(field, 'ref')}
                options={[
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ]}
                label="Escrow waiver"
                includeCheckboxes={false}
                disabled={loading}
                onChange={(val) => {
                  methods.setValue('isEscrowWaived', !!val);
                  submitIfChanged('isEscrowWaived')(undefined);
                }}
                inputClassName="min-w-0"
              />
            </InputRow>
          )}
        />
      );

    case 'propertyValue':
      return (
        <Controller
          name="propertyValue"
          control={methods.control}
          render={({ field }) => (
            <InputRow label="Property value">
              <MoneyInput
                {...omit(field, 'ref')}
                disabled={loading}
                onBlur={submitIfChanged('propertyValue')}
              />
            </InputRow>
          )}
        />
      );
    case 'remainingLoanBalance':
      return (
        <Controller
          name="remainingLoanBalance"
          control={methods.control}
          render={({ field }) => (
            <InputRow
              label="Remaining loan balance"
              labelClassName="whitespace-nowrap"
            >
              <MoneyInput
                {...omit(field, 'ref')}
                disabled={loading}
                onBlur={submitIfChanged('remainingLoanBalance')}
              />
            </InputRow>
          )}
        />
      );

    case 'cashOutAmount':
      return (
        <Controller
          name="cashOutAmount"
          control={methods.control}
          render={({ field }) => (
            <InputRow label="Cash-out amount">
              <MoneyInput
                {...omit(field, 'ref')}
                disabled={loading}
                onBlur={submitIfChanged('cashOutAmount')}
              />
            </InputRow>
          )}
        />
      );
    case 'transactionPurpose':
      return (
        <Controller
          control={methods.control}
          name="transactionPurpose"
          render={({ field }) => {
            return (
              <InputRow label="Goal">
                <DropdownSelectInput
                  {...omit(field, 'ref')}
                  options={refiOptions}
                  label="Goal"
                  includeCheckboxes={false}
                  labelVariant="plain"
                  disabled={loading}
                  onChange={(val) => {
                    const selectedPurpose = val as TransactionPurpose;
                    methods.setValue('transactionPurpose', selectedPurpose);
                    submitIfChanged('transactionPurpose')(undefined);
                  }}
                  dropdownClassName={'w-full'}
                />
              </InputRow>
            );
          }}
        />
      );
    default:
      return <></>;
  }
}

function RenderFormConfig({
  formConfig,
  submitIfChanged,
  loading,
  methods,
  occupancyTypeOptions,
  residenceTypeOptions,
  numUnitsOptions,
  ficoOptions,
  propertyType,
  setPropertyType,
}: {
  formConfig: FormConfig<InteractiveRatesFormShape>;
  occupancyTypeOptions: SelectOption[];
  residenceTypeOptions: SelectOption[];
  numUnitsOptions: SelectOption[];
  ficoOptions: SelectOption[];
  loading: boolean;
  methods: UseFormReturn<InteractiveRatesFormShape>;
  submitIfChanged: (
    name: FieldPath<InteractiveRatesFormShape>
  ) => (e?: React.FocusEvent<HTMLInputElement, Element>) => void;
  propertyType: ResidenceType | undefined;
  setPropertyType: React.Dispatch<
    React.SetStateAction<ResidenceType | undefined>
  >;
}) {
  const [additionalFiltersOpen, setAdditionalFiltersOpen] = useState(false);
  return (
    <>
      <div className="hidden lg:block">
        <div className={'flex gap-4'}>
          {formConfig.topLine.map((field, index) => {
            return (
              <RenderFormField
                field={field}
                occupancyTypeOptions={occupancyTypeOptions}
                residenceTypeOptions={residenceTypeOptions}
                numUnitsOptions={numUnitsOptions}
                ficoOptions={ficoOptions}
                loading={loading}
                methods={methods}
                submitIfChanged={submitIfChanged}
                setPropertyType={setPropertyType}
                propertyType={propertyType}
                key={index}
              />
            );
          })}
          <div
            className={twMerge(
              'typo-button border-morton-600 text-morton-600 mt-[28px] flex h-[40px] w-[83px] min-w-[83px] cursor-pointer select-none items-center rounded-[4px] border-[1px] text-center font-bold'
            )}
            onClick={() => setAdditionalFiltersOpen(!additionalFiltersOpen)}
          >
            {additionalFiltersOpen ? 'FEWER' : 'MORE'} OPTIONS
          </div>
        </div>

        {additionalFiltersOpen ? (
          <div className="flex flex-col gap-4 mt-4">
            {formConfig.expandedLines.map((line, index) => {
              return (
                <div
                  className="flex justify-between gap-4"
                  key={`${line} ${index}`}
                >
                  {line.map((field, index) => {
                    return (
                      <RenderFormField
                        field={field}
                        occupancyTypeOptions={occupancyTypeOptions}
                        residenceTypeOptions={residenceTypeOptions}
                        numUnitsOptions={numUnitsOptions}
                        ficoOptions={ficoOptions}
                        loading={loading}
                        methods={methods}
                        submitIfChanged={submitIfChanged}
                        setPropertyType={setPropertyType}
                        propertyType={propertyType}
                        key={index}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </>
  );
}

export function RatesHelocHeloanOtherState({
  transactionType,
}: {
  transactionType: TransactionPurpose;
}) {
  const isHelocHeloan =
    transactionType === TransactionPurpose.RefiHeloan ||
    transactionType === TransactionPurpose.RefiHeloc;
  return (
    <div className="mt-[22px] mb-[10px] flex flex-col items-center justify-center">
      <div className="flex gap-[24px] w-auto md:w-[495px] flex-col items-center justify-center">
        {isHelocHeloan ? (
          <img src={EquityFinancingHome} />
        ) : (
          <img src={OtherRefinancingHome} />
        )}
        <div className="typo-body font-bold text-[20px]">
          {isHelocHeloan ? (
            <>
              We’re excited to help you with your HELOC — create an
              <br />
              account to get started!
            </>
          ) : (
            <>
              We offer many types of refinancing loans — create an
              <br />
              account to explore your options!
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MobileForm({
  onSubmit,
  methods,
  loading,
  submitIfChanged,
  loanTypeOptions,
  loanTermOptions,
  ficoOptions,
  occupancyTypeOptions,
  numUnitsOptions,
  residenceTypeOptions,
  sortFieldOptionsWithDirection,
  selectedPurposeTab,
  selectedGoalTab,
  propertyType,
  setPropertyType,
  isUpdating,
  queryIsLoading,
  onResetFilters,
}: {
  occupancyTypeOptions: SelectOption[];
  numUnitsOptions: SelectOption[];
  residenceTypeOptions: SelectOption[];
  loanTypeOptions: SelectOption[];
  loanTermOptions: SelectOption[];
  ficoOptions: SelectOption[];
  sortFieldOptionsWithDirection: SelectOption[];
  loading: boolean;
  methods: UseFormReturn<InteractiveRatesFormShape>;
  onSubmit: (values: InteractiveRatesFormShape) => void;
  submitIfChanged: (
    name: FieldPath<InteractiveRatesFormShape>
  ) => (e?: React.FocusEvent<HTMLInputElement, Element>) => void;
  selectedPurposeTab: TransactionPurposeTab;
  selectedGoalTab: TransactionPurpose;
  propertyType: ResidenceType | undefined;
  setPropertyType: React.Dispatch<
    React.SetStateAction<ResidenceType | undefined>
  >;
  isUpdating: boolean;
  queryIsLoading: boolean;
  onResetFilters: () => void;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [stashedValues, setStashedValues] =
    useState<InteractiveRatesFormShape>();

  const cancel = useCallback(() => {
    // Reset to old values
    onSubmit(stashedValues!);
    setStashedValues(undefined);
    setModalOpen(false);
  }, [onSubmit, setStashedValues, stashedValues, setModalOpen]);

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        cancel();
      }
    },
    [cancel]
  );

  const submitChanges = useCallback(() => {
    onSubmit(methods.getValues());
    setModalOpen(false);
  }, [onSubmit, setModalOpen, methods]);

  const flatPurchaseFormConfig = [
    ...purchaseFormConfig.topLine,
    ...purchaseFormConfig.expandedLines.flat(),
  ];
  const flatCashOutFormConfig = [
    ...cashOutRefinanceFormConfig.topLine,
    ...cashOutRefinanceFormConfig.expandedLines.flat(),
  ];
  const flatRateTermFormConfig = [
    ...rateTermRefinanceFormConfig.topLine,
    ...rateTermRefinanceFormConfig.expandedLines.flat(),
  ];

  const isCashOut = selectedGoalTab === TransactionPurpose.RefiCashOut;
  const isHeloc = selectedGoalTab === TransactionPurpose.RefiHeloc;
  const flatRefiFormConfig = isCashOut
    ? flatCashOutFormConfig
    : flatRateTermFormConfig;
  const isRefinance = selectedPurposeTab === TransactionPurposeTab.Refinance;

  return (
    <>
      <Dialog.Root open={modalOpen} onOpenChange={onOpenChange}>
        {isHeloc ? null : (
          <div
            className={classNames(
              'flex justify-center mt-[13px]',
              isUpdating || queryIsLoading
                ? 'pointer-events-none cursor-not-allowed text-sidewalk-300 hover:bg-white'
                : ''
            )}
          >
            <Button
              onClick={() => {
                const stashed = methods.getValues();
                console.log({ stashedValues });
                setStashedValues(methods.getValues());
                setModalOpen(true);
              }}
              variant="text"
              className="flex items-center gap-2 bg-transparent"
              disabled={isUpdating || queryIsLoading}
            >
              <DetailsIcon className="ml-1 mt-[-1px] h-[18px] w-[18px]" />
              Edit details
            </Button>
          </div>
        )}
        <Dialog.Portal>
          <Dialog.Overlay className="data-[state=open]:animate-overlayShow fixed inset-0 bg-black" />
          <Dialog.Content className="bg-hardwood-30 data-[state=open]:animate-contentShow fixed top-0 z-[100] h-full max-h-full w-full max-w-full  focus:outline-none">
            <Dialog.Title className="border-b-sidewalk-50 typo-heading-5 text-black-900 flex h-[60px] w-full items-center justify-center border-b-[1px] bg-white text-center font-sans font-bold">
              <Dialog.Close asChild>
                <CloseIcon className="text-sidewalk-900 absolute left-6" />
              </Dialog.Close>
              <h5>Edit details</h5>
            </Dialog.Title>

            <div className="p-[25px] overflow-y-auto max-h-[calc(100vh-200px)]">
              <div className="flex flex-row items-center gap-2">
                <Controller
                  control={methods.control}
                  name="selectedIqLoanCategories"
                  render={({ field }) => {
                    return (
                      <DropdownSelectInput
                        hideSelection
                        labelWrapperClassName={twMerge(
                          'lg:!w-auto',
                          dropdownSelectLabelClassName(
                            field.value,
                            loanTypeOptions
                          )
                        )}
                        className="w-[100px]"
                        labelClassName="font-normal"
                        options={loanTypeOptions}
                        label="Loan type"
                        labelVariant="pill"
                        multiple
                        disabled={loading}
                        {...omit(field, 'ref')}
                        changeOnClose
                        onChange={(val) => {
                          methods.setValue(
                            'selectedIqLoanCategories',
                            val as InteractiveQuoteLoanCategory[]
                          );
                          methods.setValue('clearFilters', false);
                          submitIfChanged('selectedIqLoanCategories')(
                            undefined
                          );
                        }}
                      />
                    );
                  }}
                />
                <Controller
                  control={methods.control}
                  name="selectedLoanTerms"
                  render={({ field }) => {
                    return (
                      <DropdownSelectInput
                        hideSelection
                        labelWrapperClassName={twMerge(
                          'lg:!w-auto',
                          dropdownSelectLabelClassName(
                            field.value,
                            loanTermOptions
                          )
                        )}
                        className="w-[69px]"
                        labelClassName="font-normal"
                        options={loanTermOptions}
                        label="Term"
                        labelVariant="pill"
                        multiple
                        disabled={loading}
                        {...omit(field, 'ref')}
                        changeOnClose
                        onChange={(val) => {
                          methods.setValue(
                            'selectedLoanTerms',
                            val as LoanTerm[]
                          );
                          methods.setValue('clearFilters', false);
                          submitIfChanged('selectedLoanTerms')(undefined);
                        }}
                      />
                    );
                  }}
                />
                <Controller
                  control={methods.control}
                  name="sortField"
                  render={({ field }) => {
                    return (
                      <DropdownSelectInput
                        hideSelection
                        labelClassName="font-normal"
                        labelWrapperClassName={twMerge('lg:!w-auto')}
                        className="w-auto sm:relative"
                        buttonClassName="w-auto flex"
                        options={sortFieldOptionsWithDirection}
                        label="Sort"
                        labelVariant="pill"
                        disabled={loading}
                        includeCheckboxes={false}
                        {...omit(field, 'ref')}
                        changeOnClose
                        onChange={(val) => {
                          methods.setValue('sortField', val as SortField);
                          methods.setValue('clearFilters', false);
                          submitIfChanged('sortField')(undefined);
                        }}
                      />
                    );
                  }}
                />
                <div className="flex w-[24px] h-[24px] cursor-pointer rounded-full bg-hardwood-100 hover:bg-hardwood-200 items-center justify-center p-[6px]">
                  <img
                    className={classNames(
                      'text-hardwood-700 scale-x-[-1] w-[22px] h-[22px]'
                    )}
                    src={ResetIcon}
                    onClick={onResetFilters}
                  />
                </div>
              </div>
              {isRefinance ? (
                <>
                  {flatRefiFormConfig.map((field, index) => {
                    return (
                      <div
                        className="mt-[16px]"
                        key={`${field.toString()}-${index}`}
                      >
                        <RenderFormField
                          field={field}
                          occupancyTypeOptions={occupancyTypeOptions}
                          residenceTypeOptions={residenceTypeOptions}
                          numUnitsOptions={numUnitsOptions}
                          ficoOptions={ficoOptions}
                          loading={loading}
                          methods={methods}
                          submitIfChanged={submitIfChanged}
                          propertyType={propertyType}
                          setPropertyType={setPropertyType}
                          key={index}
                        />
                      </div>
                    );
                  })}
                </>
              ) : (
                <>
                  {flatPurchaseFormConfig.map((field, index) => {
                    return (
                      <div
                        className="mt-[16px]"
                        key={`${field.toString()}-${index}`}
                      >
                        <RenderFormField
                          field={field}
                          occupancyTypeOptions={occupancyTypeOptions}
                          residenceTypeOptions={residenceTypeOptions}
                          numUnitsOptions={numUnitsOptions}
                          ficoOptions={ficoOptions}
                          loading={loading}
                          methods={methods}
                          submitIfChanged={submitIfChanged}
                          propertyType={propertyType}
                          setPropertyType={setPropertyType}
                          key={index}
                        />
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            <div className="fixed bottom-[env(safe-area-inset-bottom)] left-0 right-0 border-t-sidewalk-200 flex flex-col justify-end border-t-[1px] pt-[25px] px-[25px] bg-white">
              <Button onClick={() => submitChanges()} className="w-full">
                UPDATE RATES
              </Button>

              <Button
                variant="text"
                onClick={() => cancel()}
                className="w-full bg-transparent"
              >
                CANCEL
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

export const RefinanceFieldset: React.FC<{
  occupancyTypeOptions: SelectOption[];
  residenceTypeOptions: SelectOption[];
  numUnitsOptions: SelectOption[];
  ficoOptions: SelectOption[];
  loading: boolean;
  methods: UseFormReturn<InteractiveRatesFormShape>;
  hideFieldSet: boolean;
  submitIfChanged: (
    name: FieldPath<InteractiveRatesFormShape>
  ) => (e?: React.FocusEvent<HTMLInputElement, Element>) => void;
  selectedGoalTab: TransactionPurpose;
  propertyType: ResidenceType | undefined;
  setPropertyType: React.Dispatch<
    React.SetStateAction<ResidenceType | undefined>
  >;
}> = ({
  submitIfChanged,
  loading,
  methods,
  occupancyTypeOptions,
  residenceTypeOptions,
  numUnitsOptions,
  ficoOptions,
  hideFieldSet,
  selectedGoalTab,
  setPropertyType,
  propertyType,
}) => {
  const isCashOut = selectedGoalTab === TransactionPurpose.RefiCashOut;
  return (
    <>
      {hideFieldSet ? null : (
        <RenderFormConfig
          formConfig={
            isCashOut ? cashOutRefinanceFormConfig : rateTermRefinanceFormConfig
          }
          occupancyTypeOptions={occupancyTypeOptions}
          residenceTypeOptions={residenceTypeOptions}
          numUnitsOptions={numUnitsOptions}
          ficoOptions={ficoOptions}
          loading={loading}
          methods={methods}
          submitIfChanged={submitIfChanged}
          setPropertyType={setPropertyType}
          propertyType={propertyType}
        />
      )}
    </>
  );
};

export const PurchaseFieldset: React.FC<{
  occupancyTypeOptions: SelectOption[];
  residenceTypeOptions: SelectOption[];
  numUnitsOptions: SelectOption[];
  ficoOptions: SelectOption[];
  loading: boolean;
  methods: UseFormReturn<InteractiveRatesFormShape>;
  submitIfChanged: (
    name: FieldPath<InteractiveRatesFormShape>
  ) => (e?: React.FocusEvent<HTMLInputElement, Element>) => void;
  propertyType: ResidenceType | undefined;
  setPropertyType: React.Dispatch<
    React.SetStateAction<ResidenceType | undefined>
  >;
}> = ({
  submitIfChanged,
  loading,
  methods,
  occupancyTypeOptions,
  residenceTypeOptions,
  numUnitsOptions,
  ficoOptions,
  propertyType,
  setPropertyType,
}) => {
  return (
    <RenderFormConfig
      formConfig={purchaseFormConfig}
      occupancyTypeOptions={occupancyTypeOptions}
      residenceTypeOptions={residenceTypeOptions}
      numUnitsOptions={numUnitsOptions}
      ficoOptions={ficoOptions}
      loading={loading}
      methods={methods}
      submitIfChanged={submitIfChanged}
      propertyType={propertyType}
      setPropertyType={setPropertyType}
    />
  );
};

export const InteractiveRatesForm: React.FC<InteractiveRatesFormProps> = ({
  selectedGoalTab,
  setSelectedGoalTab,
  setSelectedPurposeTab,
  selectedPurposeTab,
  ficoOptions,
  loanTypeOptions,
  loanTermOptions,
  occupancyTypeOptions,
  residenceTypeOptions,
  numUnitsOptions,
  sortFieldOptions,
  defaultValues,
  fieldErrors,
  onSubmit,
  loading,
  sliderRanges,
  rates,
  queryIsLoading,
  ctaLink,
  isUpdating,
  filterDefaults,
}) => {
  const isTabletWidth = useTabletWidth();
  const isMobileWidth = usePhoneXlWidth();
  const methods = useZodForm({
    schema: interactiveRatesFormValidationSchema,
  });
  const sliderValues = useSliderRanges(methods, sliderRanges);
  const initialPropertyType = methods.getValues('residenceType');
  const [propertyType, setPropertyType] = useState<ResidenceType | undefined>(
    initialPropertyType
  );

  useEffect(() => {
    methods.reset(defaultValues as InteractiveRatesFormShape);
  }, [defaultValues, methods]);

  useEffect(() => {
    if (fieldErrors) {
      Object.keys(fieldErrors).forEach((_key) => {
        const key = _key as keyof FieldErrors<InteractiveRatesFormShape>;

        methods.setError(
          key as keyof FieldErrors<InteractiveRatesFormShape>,
          { type: 'custom', message: fieldErrors[key] as any },
          { shouldFocus: false }
        );
      });
    }
  }, [fieldErrors, methods]);

  const submitIfChanged = useCallback(
    (name: FieldPath<InteractiveRatesFormShape>) => {
      return (e?: React.FocusEvent<HTMLInputElement, Element>) => {
        const newValue = methods.getValues(name);
        const d = (defaultValues as any)[name];
        if (newValue !== (defaultValues as any)[name]) {
          onSubmit(methods.getValues());
        }
      };
    },
    [defaultValues, methods]
  );

  const sortDirection = methods.getValues('sortDir');
  const sortFieldOptionsWithDirection: SelectOption[] = sortFieldOptions.map(
    (option) => {
      return {
        ...option,
        label: `${option.label} ${
          sortDirection === SortDirection.Desc
            ? '(high to low)'
            : '(low to high)'
        }`,
      };
    }
  );

  const onResetFilters = () => {
    methods.setValue('clearFilters', true);
    submitIfChanged('clearFilters')(undefined);
    onSubmit(methods.getValues());
  };

  const isPurchase = selectedGoalTab === TransactionPurpose.Purchase;
  const isRefiCashout = selectedGoalTab === TransactionPurpose.RefiCashOut;
  const isRefiLowerPayment =
    selectedGoalTab === TransactionPurpose.RefiLowerPayment;
  const isRefiChangeTerms =
    selectedGoalTab === TransactionPurpose.RefiChangeTerms;

  const DesktopComplexSliderFieldSet: React.FC = () => {
    if (isRefiCashout) {
      return (
        <Controller
          control={methods.control}
          name="equityMax"
          render={() => {
            let sliderVal = [sliderValues.equity.min, sliderValues.equity.max];
            return (
              <div className="w-auto">
                <DropdownRangeSliderInput
                  name="data.equityMax"
                  className="w-auto max-w-[200px]"
                  labelClassName="font-normal mr-[2px]"
                  labelWrapperClassName={classNames(
                    filterDefaults.equityIsDefault === false
                      ? 'bg-morton-300 text-morton-700'
                      : 'bg-hardwood-100 text-hardwood-700 hover:bg-hardwood-200 active:bg-hardwood-300'
                  )}
                  selectArrowIcon={<img src={SelectArrow} />}
                  openWrapperClassName="bg-morton-300 text-morton-700"
                  label={'Equity after refinancing'}
                  labelTooltip={
                    <LabelTooltip
                      tooltipClassName={
                        'typo-body-small max-w-[300px] text-left'
                      }
                      iconClassName={classNames(
                        'text-[12px] mb-[1px] ml-[2px] mt-[2px] cursor-pointer',
                        filterDefaults.equityIsDefault === false
                          ? 'text-morton-700'
                          : 'text-hardwood-400'
                      )}
                    >
                      If you’d like to maintain your current home equity after
                      refinancing, select a similar range here.
                      <br />A related term is your loan-to-value ratio — the
                      loan amount relative to your home’s value. LTV is the
                      inverse of your equity. For example, if your home equity
                      is 30%, your LTV is 70%.
                    </LabelTooltip>
                  }
                  disabled={loading}
                  min={sliderRanges?.equityRange[0] ?? 0}
                  max={sliderRanges?.equityRange[1] ?? 100}
                  step={0.1}
                  marker={undefined}
                  //@ts-ignore
                  value={sliderVal}
                  changeOnClose
                  onChange={(val: number[]) => {
                    if (val) {
                      methods.setValue('equityMin', val[0]);
                      methods.setValue('equityMax', val[1]);
                    }
                    methods.setValue('clearFilters', false);
                    submitIfChanged('equityMin')(undefined);
                    submitIfChanged('equityMax')(undefined);
                  }}
                  hidePointsCreditsLabel={true}
                  formatter={(val) => `${val}%`}
                />
              </div>
            );
          }}
        />
      );
    } else if (isRefiLowerPayment || isRefiChangeTerms) {
      return (
        <Controller
          control={methods.control}
          name="closingCostsMax"
          render={() => {
            let sliderVal = [
              sliderValues.closingCosts.min,
              sliderValues.closingCosts.max,
            ];
            return (
              <div className="w-auto">
                <DropdownRangeSliderInput
                  name="data.loanCostsMax"
                  className="w-auto max-w-[120px]"
                  labelClassName="font-normal mr-2"
                  labelWrapperClassName={classNames(
                    filterDefaults.closingCostsIsDefault === false
                      ? 'bg-morton-300 text-morton-700'
                      : 'bg-hardwood-100 text-hardwood-700 hover:bg-hardwood-200 active:bg-hardwood-300'
                  )}
                  selectArrowIcon={<img src={SelectArrow} />}
                  openWrapperClassName="bg-morton-300 text-morton-700"
                  label="Closing costs"
                  disabled={loading}
                  min={sliderRanges?.closingCostsRange[0] ?? 0}
                  max={sliderRanges?.closingCostsRange[1] ?? 0}
                  step={50}
                  marker={undefined}
                  //@ts-ignore
                  value={sliderVal}
                  changeOnClose
                  onChange={(val: number[]) => {
                    if (val) {
                      methods.setValue('closingCostsMin', val[0]);
                      methods.setValue('closingCostsMax', val[1]);
                    }
                    methods.setValue('clearFilters', false);
                    submitIfChanged('closingCostsMin')(undefined);
                    submitIfChanged('closingCostsMax')(undefined);
                  }}
                  hidePointsCreditsLabel={true}
                  formatter={(val) =>
                    formatCurrency(val, { maximumFractionDigits: 0 })
                  }
                />
              </div>
            );
          }}
        />
      );
    } else if (isPurchase) {
      return (
        <Controller
          control={methods.control}
          name="loanCostsMax"
          render={() => {
            let sliderVal = [
              sliderValues.totalLoanCosts.min ?? null,
              sliderValues.totalLoanCosts.max ?? null,
            ];
            return (
              <div className="w-[106px]">
                <DropdownRangeSliderInput
                  name="data.loanCostsMax"
                  className="w-auto max-w-[120px]"
                  labelClassName="font-normal mr-2"
                  labelWrapperClassName={classNames(
                    filterDefaults.loanCostsIsDefault === false
                      ? 'bg-morton-300 text-morton-700'
                      : 'bg-hardwood-100 text-hardwood-700 hover:bg-hardwood-200 active:bg-hardwood-300'
                  )}
                  selectArrowIcon={<img src={SelectArrow} />}
                  openWrapperClassName="bg-morton-300 text-morton-700"
                  label="Loan costs"
                  disabled={loading}
                  min={sliderRanges?.totalLoanCostsRange[0] ?? 0}
                  max={sliderRanges?.totalLoanCostsRange[1] ?? 0}
                  step={50}
                  marker={undefined}
                  //@ts-ignore
                  value={sliderVal}
                  changeOnClose
                  onChange={(val: number[]) => {
                    if (val) {
                      methods.setValue('loanCostsMin', val[0]);
                      methods.setValue('loanCostsMax', val[1]);
                    }
                    methods.setValue('clearFilters', false);
                    submitIfChanged('loanCostsMin')(undefined);
                    submitIfChanged('loanCostsMax')(undefined);
                  }}
                  hidePointsCreditsLabel={true}
                  formatter={(val) =>
                    formatCurrency(val, {
                      maximumFractionDigits: 0,
                    })
                  }
                />
              </div>
            );
          }}
        />
      );
    } else {
      return <></>;
    }
  };

  const FilterFieldset = () => {
    return (
      <div className="flex w-full gap-4">
        <div className="flex w-full justify-start">
          <div className="flex flex-col md:flex-row items-center gap-2 w-full">
            {isMobileWidth ? (
              <div className="flex flex-col w-full" />
            ) : (
              <>
                <div className="flex flex-row items-center gap-2">
                  <Controller
                    control={methods.control}
                    name="selectedIqLoanCategories"
                    render={({ field }) => {
                      return (
                        <DropdownSelectInput
                          hideSelection
                          labelWrapperClassName={twMerge(
                            'lg:!w-auto',
                            dropdownSelectLabelClassName(
                              field.value,
                              loanTypeOptions
                            )
                          )}
                          className="w-[100px]"
                          labelClassName="font-normal"
                          options={loanTypeOptions}
                          label="Loan type"
                          labelVariant="pill"
                          multiple
                          disabled={loading}
                          {...omit(field, 'ref')}
                          changeOnClose
                          onChange={(val) => {
                            methods.setValue(
                              'selectedIqLoanCategories',
                              val as InteractiveQuoteLoanCategory[]
                            );
                            methods.setValue('clearFilters', false);

                            submitIfChanged('selectedIqLoanCategories')(
                              undefined
                            );
                          }}
                        />
                      );
                    }}
                  />
                  <Controller
                    control={methods.control}
                    name="selectedLoanTerms"
                    render={({ field }) => {
                      return (
                        <DropdownSelectInput
                          hideSelection
                          labelWrapperClassName={twMerge(
                            'lg:!w-auto',
                            dropdownSelectLabelClassName(
                              field.value,
                              loanTermOptions
                            )
                          )}
                          className="w-[69px]"
                          labelClassName="font-normal"
                          options={loanTermOptions}
                          label="Term"
                          labelVariant="pill"
                          multiple
                          disabled={loading}
                          {...omit(field, 'ref')}
                          changeOnClose
                          onChange={(val) => {
                            methods.setValue(
                              'selectedLoanTerms',
                              val as LoanTerm[]
                            );
                            methods.setValue('clearFilters', false);

                            submitIfChanged('selectedLoanTerms')(undefined);
                          }}
                        />
                      );
                    }}
                  />
                  <Controller
                    control={methods.control}
                    name="interestRateMax"
                    render={() => {
                      let sliderVal = [
                        sliderValues.interestRate.min,
                        sliderValues.interestRate.max,
                      ];
                      return (
                        <div className="w-auto">
                          <DropdownRangeSliderInput
                            name="data.interestRateMax"
                            className="w-auto max-w-[64px]"
                            labelClassName="font-normal mr-2"
                            labelWrapperClassName={classNames(
                              filterDefaults.interestRateIsDefault === false
                                ? 'bg-morton-300 text-morton-700'
                                : 'bg-hardwood-100 text-hardwood-700 hover:bg-hardwood-200 active:bg-hardwood-300'
                            )}
                            selectArrowIcon={<img src={SelectArrow} />}
                            openWrapperClassName="bg-morton-300 text-morton-700"
                            label="Rate"
                            disabled={loading}
                            min={sliderRanges?.interestRatesRange[0] ?? 0}
                            max={sliderRanges?.interestRatesRange[1] ?? 0}
                            step={0.01}
                            marker={undefined}
                            //@ts-ignore
                            value={sliderVal}
                            changeOnClose
                            onChange={(val: number[]) => {
                              if (val) {
                                methods.setValue('interestRateMin', val[0]);
                                methods.setValue('interestRateMax', val[1]);
                              }
                              methods.setValue('clearFilters', false);
                              submitIfChanged('interestRateMin')(undefined);
                              submitIfChanged('interestRateMax')(undefined);
                            }}
                            hidePointsCreditsLabel={true}
                            formatter={(val) => `${val}%`}
                          />
                        </div>
                      );
                    }}
                  />
                </div>
                <div className="flex flex-row items-center gap-2">
                  <Controller
                    control={methods.control}
                    name="principalAndInterestMax"
                    render={() => {
                      let sliderVal = [
                        sliderValues.principalAndInterest.min,
                        sliderValues.principalAndInterest.max,
                      ];
                      return (
                        <div className="w-[155px]">
                          <DropdownRangeSliderInput
                            name="data.principalAndInterestMax"
                            className="w-auto"
                            labelClassName="font-normal mr-2"
                            labelWrapperClassName={classNames(
                              filterDefaults.principalAndInterestIsDefault ===
                                false
                                ? 'bg-morton-300 text-morton-700'
                                : 'bg-hardwood-100 text-hardwood-700 hover:bg-hardwood-200 active:bg-hardwood-300'
                            )}
                            selectArrowIcon={<img src={SelectArrow} />}
                            openWrapperClassName="bg-morton-300 text-morton-700"
                            label="Principal &amp; interest"
                            disabled={loading}
                            min={
                              sliderRanges?.principalAndInterestRange[0] ?? 0
                            }
                            max={
                              sliderRanges?.principalAndInterestRange[1] ?? 0
                            }
                            step={50}
                            marker={undefined}
                            //@ts-ignore
                            value={sliderVal}
                            changeOnClose
                            onChange={(val: number[]) => {
                              if (val) {
                                methods.setValue(
                                  'principalAndInterestMin',
                                  val[0]
                                );
                                methods.setValue(
                                  'principalAndInterestMax',
                                  val[1]
                                );
                              }
                              methods.setValue('clearFilters', false);
                              submitIfChanged('principalAndInterestMin')(
                                undefined
                              );
                              submitIfChanged('principalAndInterestMax')(
                                undefined
                              );
                            }}
                            hidePointsCreditsLabel={true}
                            formatter={(val) =>
                              formatCurrency(val, {
                                maximumFractionDigits: 0,
                              })
                            }
                          />
                        </div>
                      );
                    }}
                  />
                  <DesktopComplexSliderFieldSet />
                  <div className="flex w-[24px] h-[24px] cursor-pointer rounded-full bg-hardwood-100 hover:bg-hardwood-200 items-center justify-center p-[6px]">
                    <img
                      className={classNames(
                        'text-hardwood-700 scale-x-[-1] w-[22px] h-[22px]'
                      )}
                      src={ResetIcon}
                      onClick={onResetFilters}
                    />
                  </div>
                </div>
                <div className="w-full flex flex-1 justify-start md:justify-end">
                  <Controller
                    control={methods.control}
                    name="sortField"
                    render={({ field }) => {
                      return (
                        <DropdownSelectInput
                          hideSelection
                          labelClassName="font-normal"
                          labelWrapperClassName={twMerge('lg:!w-auto')}
                          className="w-auto sm:relative"
                          buttonClassName="w-auto flex justify-self-end sm:justify-self-start"
                          options={sortFieldOptionsWithDirection}
                          label="Sort"
                          labelVariant="pill"
                          disabled={loading}
                          includeCheckboxes={false}
                          {...omit(field, 'ref')}
                          changeOnClose
                          onChange={(val) => {
                            methods.setValue('sortField', val as SortField);
                            submitIfChanged('sortField')(undefined);
                          }}
                        />
                      );
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };
  const isHelocHeloanOrOther =
    selectedGoalTab === TransactionPurpose.RefiHeloan ||
    selectedGoalTab === TransactionPurpose.RefiHeloc ||
    selectedGoalTab === TransactionPurpose.RefiOther;

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <Tabs.Root
          value={selectedPurposeTab}
          onValueChange={(value) => {
            const tab = value as TransactionPurposeTab;
            const transactionPurpose = getTransactionPurpose(tab);
            methods.setValue('transactionPurpose', transactionPurpose);
            setSelectedPurposeTab(tab);
            setSelectedGoalTab(transactionPurpose);
            onSubmit(methods.getValues());
          }}
        >
          <Tabs.List
            className="relative -mb-[1px] flex gap-1 px-4 sm:px-6"
            aria-label="Mortgage options"
          >
            {tabOptions.map((option, i) => {
              return (
                <Tabs.Trigger
                  key={`${option.value}-${option.label}-${i}`}
                  value={option.value}
                  className={twMerge(
                    // Base styles
                    'rounded-t-md py-2 text-base font-medium transition-colors',
                    // Mobile styles
                    'px-2 min-w-[80px] h-[40px] text-sm flex-1',
                    // Tablet/Desktop styles
                    'sm:px-8 sm:min-w-[138px] sm:h-[48px] sm:text-base sm:flex-none',
                    // State styles
                    'data-[state=active]:bg-hardwood-30 data-[state=active]:text-sidewalk-900 data-[state=active]:font-bold',
                    'data-[state=inactive]:bg-hardwood-100 data-[state=inactive]:text-sidewalk-500',
                    'hover:bg-hardwood-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-morton-600'
                  )}
                >
                  {option.label}
                </Tabs.Trigger>
              );
            })}
          </Tabs.List>
          <div
            className={'rounded-t-lg bg-hardwood-30 p-2 sm:p-[48px_64px_10px]'}
          >
            {isTabletWidth ? (
              <MobileForm
                onSubmit={onSubmit}
                occupancyTypeOptions={occupancyTypeOptions}
                residenceTypeOptions={residenceTypeOptions}
                numUnitsOptions={numUnitsOptions}
                ficoOptions={ficoOptions}
                loanTermOptions={loanTermOptions}
                loanTypeOptions={loanTypeOptions}
                sortFieldOptionsWithDirection={sortFieldOptionsWithDirection}
                methods={methods}
                submitIfChanged={submitIfChanged}
                loading={loading}
                selectedPurposeTab={selectedPurposeTab}
                selectedGoalTab={selectedGoalTab}
                setPropertyType={setPropertyType}
                propertyType={propertyType}
                isUpdating={isUpdating}
                queryIsLoading={queryIsLoading}
                onResetFilters={onResetFilters}
              />
            ) : selectedPurposeTab === TransactionPurposeTab.Purchase ? (
              <PurchaseFieldset
                occupancyTypeOptions={occupancyTypeOptions}
                residenceTypeOptions={residenceTypeOptions}
                numUnitsOptions={numUnitsOptions}
                ficoOptions={ficoOptions}
                methods={methods}
                submitIfChanged={submitIfChanged}
                loading={loading}
                setPropertyType={setPropertyType}
                propertyType={propertyType}
              />
            ) : (
              <RefinanceFieldset
                occupancyTypeOptions={occupancyTypeOptions}
                residenceTypeOptions={residenceTypeOptions}
                numUnitsOptions={numUnitsOptions}
                ficoOptions={ficoOptions}
                methods={methods}
                submitIfChanged={submitIfChanged}
                loading={loading}
                hideFieldSet={isHelocHeloanOrOther}
                selectedGoalTab={selectedGoalTab}
                setPropertyType={setPropertyType}
                propertyType={propertyType}
              />
            )}

            {isHelocHeloanOrOther ? null : (
              <>
                <div className="my-0 sm:my-[32px] flex w-full justify-between flex-col sm:flex-row">
                  <FilterFieldset />
                </div>
              </>
            )}
            {isUpdating && !isHelocHeloanOrOther ? (
              <div className="mt-8 flex flex-col gap-4">
                <div className="flex justify-center">
                  <div className="w-full h-[209px] flex items-center justify-center">
                    <img
                      src={LoadingSpinner}
                      height={37}
                      className="animate-spin"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <RatesToolDisplayContainer
                rates={rates}
                selectedPurposeTab={selectedPurposeTab}
                ctaLink={ctaLink}
                queryIsLoading={queryIsLoading}
                isHelocHeloanOrOther={isHelocHeloanOrOther}
              />
            )}
          </div>
        </Tabs.Root>
      </form>
    </FormProvider>
  );
};
